/**
 * SetupStageChecklist — read-only progress view of a LocalComputer's
 * curl-onboarding stages.
 *
 * Per Alexey's verbatim spec (xxvii / task #82 / commit 4e2): each
 * stage renders with EXACTLY one of four states.
 *
 *   - **success** (✓ green) — stage completed; `i ≤ idx` where
 *                              `idx = SETUP_STAGES_ORDERED.indexOf(setup_stage)`.
 *                              (`setup_stage` is the highest-reached
 *                              milestone, so it counts as done.)
 *   - **failure** (✗ red)   — recent `last_setup_error` is recorded
 *                              against the next-to-do stage.
 *   - **running** (spinner) — ONLY when something is actually running
 *                              on the jetson right now. Signal:
 *                              `lc.updated_at` (auto_now on save —
 *                              bumps when the BE auto-advances
 *                              setup_stage) within
 *                              `RUNNING_WINDOW_SEC`. The next-to-do
 *                              stage is the candidate; this state
 *                              only fires when the watermark is fresh
 *                              AND no recent error is on record.
 *   - **pending** (empty ring) — everything else. Includes:
 *                                  (a) next-to-do stage with no
 *                                      recent activity → pending user
 *                                      action (run curl, etc.),
 *                                  (b) further-future stages.
 *                              Muted styling distinguishes (b) from (a).
 *
 * The pre-4e2 "in_progress on next-pending whenever idx > 0" pattern is
 * gone — it was lying about progress when the script silent-exited.
 * "running" now requires a real watermark, not just "we've started".
 *
 * Used by both `LocalComputerDetailPage` (full LC, has updated_at) and
 * the setup-master wizard's `JetsonStageBody` (passes
 * `LocalComputerSummary`, which doesn't include updated_at — running
 * detection silently degrades to never-running for the wizard until
 * the summary serializer is extended; acceptable since the wizard's
 * heartbeat-based spinner was already removed in commit 4b).
 */
import { Check, Loader2, XCircle } from 'lucide-react';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import {
  SETUP_STAGES_ORDERED,
  type SetupError,
  type SetupStage,
} from '@/api/localComputers';

/**
 * How fresh `last_setup_error.at` must be to flip the next-to-do
 * stage to `failure` (rather than `pending` or `running`). BE's
 * `last_setup_error` is sticky — keep the window tight. 5 min covers
 * an active curl session without leaving stale errors lit up.
 */
const FAILURE_WINDOW_MIN = 5;

/**
 * How fresh `lc.updated_at` must be to render the next-to-do stage
 * as `running`. The BE's `updated_at` is auto_now, so it bumps when
 * the BE auto-advances setup_stage (the only signal of "the script
 * just made progress"). Operator PATCHes also bump updated_at — a
 * minor false positive (operator edits a label → spinner flashes on
 * the next stage for 90s) that Alexey explicitly accepted in xxvii.
 * 90s window covers the script's poll cadence (~30s) with slack.
 */
const RUNNING_WINDOW_SEC = 90;

type Status = 'success' | 'failure' | 'running' | 'pending';

interface Props {
  /**
   * Accepts any object exposing `setup_stage` (required) + optional
   * `updated_at` (ISO 8601). The detail page passes the full
   * `LocalComputer`; the wizard passes `LocalComputerSummary` which
   * lacks `updated_at` — running detection silently degrades there.
   */
  lc: { setup_stage: SetupStage; updated_at?: string | null };
  /**
   * Latest setup-script error. When recent (≤ FAILURE_WINDOW_MIN), the
   * next-to-do stage flips to `failure`.
   */
  lastSetupError?: SetupError | null;
  /**
   * When defined, prepends a synthetic "Параметры заполнены" row at
   * the top of the checklist. False → row renders as `pending` (the
   * operator's current attention target); true → success. Matches
   * xxvii §4.FE: "the first step in the setup steps section should
   * be still incomplete [if params missing]". Omit (undefined) on
   * surfaces where this conceptual step doesn't apply (e.g. wizard's
   * STAGE 1 card, which has its own params plumbing).
   */
  requiredParamsFilled?: boolean;
}

export default function SetupStageChecklist({
  lc,
  lastSetupError,
  requiredParamsFilled,
}: Props) {
  const { t } = useT();
  const idx = SETUP_STAGES_ORDERED.indexOf(lc.setup_stage);
  const hasFutureStage = idx + 1 < SETUP_STAGES_ORDERED.length;
  const nextRowIdx = hasFutureStage ? idx + 1 : -1;
  const showParamsRow = requiredParamsFilled !== undefined;
  // When the params row is shown AND params are NOT filled, IT is
  // the operator's attention target — the BE stages below stay muted
  // until params land. When params ARE filled (or the row isn't
  // shown), the next-to-do BE stage carries the attention.
  const paramsRowIsPending = showParamsRow && !requiredParamsFilled;

  // Single source of truth via computeNextRowState — keeps the
  // checklist's next-row + the detail page's Section 3 body in
  // lockstep (including the manual-stages-never-running carve-out).
  const { status: beNextRowStatus } = computeNextRowState(lc, lastSetupError);

  // Demote the BE next-row to far-future-pending while the params
  // row is still pending — visually only one row at a time should
  // read as "current attention".
  const nextRowStatus: Status = paramsRowIsPending ? 'pending' : beNextRowStatus;

  // Params row position fix per Alexey 2026-05-23 live test:
  // chronologically the LC reaches ENROLLED via discovery first
  // (Регистрация ✓), THEN the operator fills params, THEN the BE
  // advances WG_CONFIGURED → … → DOCKER_RUNNING. So the params row
  // belongs BETWEEN ENROLLED and WG_CONFIGURED — not at the very
  // top where it visually preceded the already-completed
  // Регистрация row. Pre-fix the unconditional prepend made the
  // done-stage Регистрация sandwich awkwardly between the current
  // attention target (Параметры) and the future stages.
  const enrolledIdx = SETUP_STAGES_ORDERED.indexOf('ENROLLED');
  const renderBeStage = (s: SetupStage, i: number) => {
    const status: Status =
      i <= idx
        ? 'success'
        : i === nextRowIdx
          ? nextRowStatus
          : 'pending';
    const isFarFuture =
      (i > nextRowIdx && nextRowIdx !== -1) || paramsRowIsPending;
    return (
      <li key={s} className="flex items-center gap-2 text-xs">
        <StatusIcon status={status} />
        <span className={statusTextClass(status, isFarFuture)}>
          {t(`admin.lc.stage.${s}`)}
        </span>
      </li>
    );
  };
  const paramsRow = showParamsRow ? (
    <li key="__params__" className="flex items-center gap-2 text-xs">
      <StatusIcon status={requiredParamsFilled ? 'success' : 'pending'} />
      <span
        className={statusTextClass(
          requiredParamsFilled ? 'success' : 'pending',
          /* isFarFuture */ false,
        )}
      >
        {t('admin.lc.detail.steps.fillParams')}
      </span>
    </li>
  ) : null;

  return (
    <div className="cursor-default space-y-1.5">
      <ul className="space-y-0.5">
        {/* Stages up to and including ENROLLED — discovery already
            ticked these by the time the page renders. */}
        {SETUP_STAGES_ORDERED.slice(0, enrolledIdx + 1).map(renderBeStage)}
        {/* Synthetic params row, gating operator-fillable fields. */}
        {paramsRow}
        {/* Stages after ENROLLED — the BE-driven flow once setup
            curl runs on the jetson. */}
        {SETUP_STAGES_ORDERED.slice(enrolledIdx + 1).map((s, offset) =>
          renderBeStage(s, enrolledIdx + 1 + offset),
        )}
      </ul>
    </div>
  );
}

/**
 * Stages the BE auto-advances via the setup-script's status-endpoint
 * polls. Stages NOT in this set require external operator work
 * (GitLab CI matrix, Traefik route, deploy job) — the BE has no
 * signal for them, so `running` can never legitimately fire there;
 * they stay `pending` until the BE auto-advance lands (task flagged
 * to team-lead 2026-05-23). This carve-out also kills the
 * lc.updated_at-contamination false-positive Alexey reported
 * (operator PATCHes a label → updated_at bumps → next-pending stage
 * lit up as `running` for 90s, even though nothing is running).
 */
const AUTO_ADVANCE_STAGES = new Set<SetupStage>([
  'WG_CONFIGURED',
  'WG_HANDSHAKE_VERIFIED',
  'SSH_PUBKEY_AUTHORIZED',
  'AUTHORIZED_KEYS_PULLED',
  'REVERSE_SSH_LIVE',
]);

/** Exposed for Section 3 in the detail page so it mirrors Section 2's
 *  next-row state without duplicating logic. */
export function computeNextRowState(
  lc: { setup_stage: SetupStage; updated_at?: string | null },
  lastSetupError: SetupError | null | undefined,
): { status: Status; nextStage: SetupStage | null } {
  const idx = SETUP_STAGES_ORDERED.indexOf(lc.setup_stage);
  const hasFutureStage = idx + 1 < SETUP_STAGES_ORDERED.length;
  if (!hasFutureStage) return { status: 'success', nextStage: null };
  const next = SETUP_STAGES_ORDERED[idx + 1];
  const failureIsRecent = isRecentFailure(lastSetupError);
  if (failureIsRecent) return { status: 'failure', nextStage: next };
  // `running` only fires for stages the BE actually drives. Manual
  // stages stay `pending` regardless of updated_at recency.
  if (AUTO_ADVANCE_STAGES.has(next) && isFreshUpdatedAt(lc.updated_at ?? null)) {
    return { status: 'running', nextStage: next };
  }
  return { status: 'pending', nextStage: next };
}

function StatusIcon({ status }: { status: Status }) {
  switch (status) {
    case 'success':
      return <Check className="size-3.5 shrink-0 text-emerald-600" />;
    case 'failure':
      return <XCircle className="size-3.5 shrink-0 text-destructive" />;
    case 'running':
      return (
        <Loader2 className="size-3.5 shrink-0 animate-spin text-sky-600 dark:text-sky-400" />
      );
    case 'pending':
      return (
        <span className="inline-block size-3.5 shrink-0 rounded-full border border-muted-foreground/40" />
      );
  }
}

function statusTextClass(status: Status, isFarFuture: boolean): string {
  return cn(
    status === 'success' && 'text-foreground',
    status === 'failure' && 'font-medium text-destructive',
    status === 'running' && 'font-medium text-sky-800 dark:text-sky-200',
    // Next-to-do `pending` is the operator's attention row — keep it
    // emphasized (foreground). Far-future pending stays muted.
    status === 'pending' && !isFarFuture && 'font-medium text-foreground',
    status === 'pending' && isFarFuture && 'text-muted-foreground',
  );
}

function isRecentFailure(err: SetupError | null | undefined): boolean {
  if (!err || !err.at) return false;
  const ageMs = Date.now() - Date.parse(err.at);
  if (!Number.isFinite(ageMs) || ageMs < 0) return false;
  return ageMs <= FAILURE_WINDOW_MIN * 60_000;
}

function isFreshUpdatedAt(iso: string | null): boolean {
  if (!iso) return false;
  const ageMs = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ageMs) || ageMs < 0) return false;
  return ageMs <= RUNNING_WINDOW_SEC * 1000;
}
